import { Component, Event, EventEmitter, JSX, Prop, State, h } from '@stencil/core'
import IMask from 'imask'
import MarkdownIt from 'markdown-it'

import {
  AnswerValue,
  NoneSelected,
  Question,
  QuestionAnswer,
  QuestionOption,
  QuestionType,
  QuestionUIControlType,
  QuestionValidationRuleType,
  UnansweredValue,
  WorkersCompensationClassValue,
} from './air-question.types'
import { CheckboxEventDetail, CheckboxOption } from '../air-checkbox/air-checkbox.types'
import { Address } from '../air-address/air-address.types'
import { BusinessClass } from '../air-business-class/air-business-class.types'
import { DatepickerMode } from '../air-datepicker/air-datepicker.types'
import { InputType } from '../air-input/air-input.types'
import { MultiSelectOption } from '../air-dropdown-multi/air-dropdown-multi.types'
import { SelectOptionValue } from '../air-select-option/air-select-option.types'
import { Validation } from '../../types/status.types'
import { WorkersCompensationClass } from '../air-workers-compensation-class/air-workers-compensation-class.types'
import { isMobile } from '../../utils/matchMedia'
import { isUndefined } from '../../utils/helpers'
import { windowProvider } from '../../utils/windowProvider'

@Component({
  tag: 'air-question',
  styleUrl: 'air-question.scss',
  shadow: true,
})
export class AirQuestion {
  /** Question */
  @Prop() question!: Question

  /** Answer */
  @Prop({ mutable: true }) answer?: AnswerValue

  /** Specifies validation of question */
  @Prop() validation?: Validation

  /** Business Classes */
  @Prop() businessClasses?: BusinessClass[]

  /** Workers compensation classes */
  @Prop() workersCompensationClasses?: WorkersCompensationClass[]

  /** Render only 'questionWithinGroup' label, hides the 'question' title */
  @Prop() preferQuestionWithinGroup?: boolean = false

  @State() isMobile = false

  private toggleTrueValues = ['yes', 'true', true]
  private toggleFalseValues = ['no', 'false', false]

  /** Triggers when question value was changed and blur */
  @Event({ eventName: 'air-question-change' }) change?: EventEmitter<QuestionAnswer>

  /** Triggers when question in focus */
  @Event({ eventName: 'air-question-focus' }) focus?: EventEmitter<QuestionAnswer>

  /** Triggers when an question has lost focus */
  @Event({ eventName: 'air-question-blur' }) blur?: EventEmitter<QuestionAnswer>

  /** Triggers when an info icon was click */
  @Event({ eventName: 'air-info' }) infoClick?: EventEmitter<Question>

  componentWillLoad(): void {
    windowProvider().addEventListener('resize', this.handleResize)
    this.handleResize()
  }

  disconnectedCallback(): void {
    windowProvider().removeEventListener('resize', this.handleResize)
  }

  private handleResize = (): void => {
    this.isMobile = isMobile()
  }

  private markdownToString(): string {
    if (this.question.ui.label === undefined && this.question.required === false) {
      return MarkdownIt().render(`${this.question.question} (Optional)`)
    }
    return MarkdownIt().render(this.question.question)
  }

  private handleFocus = (event: CustomEvent<string>): void => {
    this.answer = (event.detail !== '' ? event.detail : UnansweredValue) as AnswerValue
    this.focus?.emit({ answer: this.answer, question: this.question })
    this.handleInfoClick()
  }

  private handleChange = (event: CustomEvent<string>): void => {
    this.answer = (event.detail !== '' ? event.detail : UnansweredValue) as AnswerValue
    this.change?.emit({ answer: this.answer, question: this.question })
  }

  private handleBlur = (event: CustomEvent<string>): void => {
    this.answer = (event.detail !== '' ? event.detail : UnansweredValue) as AnswerValue
    this.blur?.emit({ answer: this.answer, question: this.question })
  }

  private handleNumberFocus = (event: CustomEvent<string>): void => {
    this.answer = (event.detail !== '' ? Number(event.detail) : UnansweredValue) as AnswerValue
    this.focus?.emit({ answer: this.answer, question: this.question })
    this.handleInfoClick()
  }

  private handleNumberChange = (event: CustomEvent<string>): void => {
    this.answer = (event.detail !== '' ? Number(event.detail) : UnansweredValue) as AnswerValue
    this.change?.emit({ answer: this.answer, question: this.question })
  }

  private handleNumberBlur = (event: CustomEvent<string>): void => {
    this.answer = (event.detail !== '' ? Number(event.detail) : UnansweredValue) as AnswerValue
    this.blur?.emit({ answer: this.answer, question: this.question })
  }

  private handleSelectFocus = (event: CustomEvent<SelectOptionValue>): void => {
    this.answer = event.detail
    this.focus?.emit({ answer: this.answer, question: this.question })
    this.handleInfoClick()
  }

  private handleSelectChange = (
    event: CustomEvent<SelectOptionValue | WorkersCompensationClassValue | null>,
  ): void => {
    this.answer = event.detail
    this.change?.emit({ answer: this.answer, question: this.question })
  }

  private handleSelectBlur = (event: CustomEvent<SelectOptionValue>): void => {
    this.answer = event.detail
    this.blur?.emit({ answer: this.answer, question: this.question })
  }

  private getOptionByBooleanValue = (value: boolean): QuestionOption | undefined => {
    if (this.question.options?.length !== 2) return
    return value
      ? this.getToggleTrueOption(this.question.options)
      : this.getToggleFalseOption(this.question.options)
  }

  private handleSelectToggleFocus = (event: CustomEvent<boolean>): void => {
    this.answer = this.getOptionByBooleanValue(event.detail)?.key
    this.focus?.emit({ answer: this.answer as AnswerValue, question: this.question })
    this.handleInfoClick()
  }

  private handleSelectToggleChange = (event: CustomEvent<boolean>): void => {
    this.answer = this.getOptionByBooleanValue(event.detail)?.key
    this.change?.emit({ answer: this.answer as AnswerValue, question: this.question })
  }

  private handleSelectToggleBlur = (event: CustomEvent<boolean>): void => {
    this.answer = this.getOptionByBooleanValue(event.detail)?.key
    this.blur?.emit({ answer: this.answer as AnswerValue, question: this.question })
  }

  private handleAddressChange = (event: CustomEvent<Address>): void => {
    this.answer = event.detail as AnswerValue
    this.change?.emit({ answer: this.answer, question: this.question })
  }

  private handleAddressFocus = (event: CustomEvent<Address>): void => {
    this.answer = event.detail as AnswerValue
    this.focus?.emit({ answer: this.answer, question: this.question })
    this.handleInfoClick()
  }

  private dateFormatTransform(date: string): string | undefined {
    // Date format MM-DD-YYYY transform to YYYY-MM-DD
    if (date.length === 0) return
    const dateArray = date.split('-')
    return `${dateArray[2]}-${dateArray[0]}-${dateArray[1]}`
  }

  private dateFormatTransformReverse(date: string | undefined): string {
    // Date format YYYY-MM-DD transform to MM-DD-YYYY
    if (date === undefined) return ''
    const dateArray = date.split('-')
    return `${dateArray[1]}-${dateArray[2]}-${dateArray[0]}`
  }

  private handleDatepickerFocus = (event: CustomEvent<string>): void => {
    this.answer = this.dateFormatTransform(event.detail) as AnswerValue
    this.focus?.emit({ answer: this.answer, question: this.question })
    this.handleInfoClick()
  }

  private handleDatepickerChange = (event: CustomEvent<string>): void => {
    this.answer = this.dateFormatTransform(event.detail) as AnswerValue
    this.change?.emit({ answer: this.answer, question: this.question })
  }

  private handleDatepickerBlur = (event: CustomEvent<string>): void => {
    this.answer = this.dateFormatTransform(event.detail) as AnswerValue
    this.blur?.emit({ answer: this.answer, question: this.question })
  }

  private handleBooleanFocus = (event: CustomEvent<boolean>): void => {
    this.answer = event.detail
    this.focus?.emit({ answer: this.answer, question: this.question })
    this.handleInfoClick()
  }

  private handleBooleanChange = (event: CustomEvent<boolean>): void => {
    this.answer = event.detail
    this.change?.emit({ answer: this.answer, question: this.question })
  }

  private handleBooleanBlur = (event: CustomEvent<boolean>): void => {
    this.answer = event.detail
    this.blur?.emit({ answer: this.answer, question: this.question })
  }

  private updateCheckboxGroupAnswer = (event: CustomEvent<CheckboxEventDetail[]>): void => {
    if (!Array.isArray(event.detail)) return

    const eventAnswer = event.detail
      .filter((option: CheckboxEventDetail) => option.checked)
      .map((option: CheckboxEventDetail) => option.optionKey)

    // Handle the convertion of no selection to UnansweredValue instead of an
    // empty array.
    if (eventAnswer.length === 0) {
      this.answer = UnansweredValue
    } else if (eventAnswer.length === 1 && eventAnswer[0] === NoneSelected) {
      this.answer = []
    } else {
      this.answer = eventAnswer as AnswerValue
    }
  }

  private handleCheckboxGroupChange = (event: CustomEvent<CheckboxEventDetail[]>): void => {
    this.updateCheckboxGroupAnswer(event)
    if (this.answer === undefined) return
    this.change?.emit({ answer: this.answer, question: this.question })
  }

  private handleCheckboxGroupFocus = (event: CustomEvent<CheckboxEventDetail[]>): void => {
    this.updateCheckboxGroupAnswer(event)
    if (this.answer === undefined) return
    this.focus?.emit({ answer: this.answer, question: this.question })
    this.handleInfoClick()
  }

  private handleMultiSelectDropdownChange = (
    event: CustomEvent<MultiSelectOption['key'][] | typeof UnansweredValue>,
  ): void => {
    this.answer = event.detail as AnswerValue
    this.change?.emit({ answer: this.answer as AnswerValue, question: this.question })
  }

  private handleMultiSelectDropdownBlur = (
    event: CustomEvent<MultiSelectOption['key'][] | typeof UnansweredValue>,
  ): void => {
    this.answer = event.detail as AnswerValue
    this.blur?.emit({ answer: this.answer, question: this.question })
  }
  private handleMultiSelectDropdownFocus = (
    event: CustomEvent<MultiSelectOption['key'][] | typeof UnansweredValue>,
  ): void => {
    this.answer = event.detail as AnswerValue
    this.focus?.emit({ answer: this.answer, question: this.question })
    this.handleInfoClick()
  }

  private handleMobileInfoClick = (): void => {
    this.infoClick?.emit(this.question)
  }

  private handleInfoClick = (): void => {
    if (this.isMobile === false) {
      this.infoClick?.emit(this.question)
    }
  }

  private getLabel = (): string | undefined => {
    const { question } = this
    return question.required !== true && question.ui.label !== undefined
      ? `${question.ui.label} (Optional)`
      : question.ui.label
  }

  private renderSingleLineElement = (): JSX.Element => {
    const maskOptions = {
      mask: this.question?.ui?.mask?.replace(/_/g, '0') || '',
      lazy: false,
    }

    const attributes = {
      class: 'form-element',
      label: this.getLabel(),
      placeholder: this.question.ui?.placeholder,
      'onAir-focus': this.handleFocus,
      'onAir-change': this.handleChange,
      'onAir-blur': this.handleBlur,
      validation: this.validation,
      'mask-value': this.question?.ui?.mask !== undefined ? IMask.PIPE_TYPE.UNMASKED : undefined,
      maskOptions: this.question?.ui?.mask !== undefined ? maskOptions : undefined,
      value: this.answer as string,
    }
    return <air-input {...attributes}></air-input>
  }

  private renderMultiLineElement = (): JSX.Element => {
    const attributes = {
      class: 'form-element',
      label: this.getLabel(),
      placeholder: this.question.ui?.placeholder,
      'onAir-focus': this.handleFocus,
      'onAir-change': this.handleChange,
      'onAir-blur': this.handleBlur,
      validation: this.validation,
      value: this.answer as string,
    }
    return <air-textarea {...attributes}></air-textarea>
  }

  private renderNumberElement = (): JSX.Element => {
    const type: InputType = 'number'
    const attributes = {
      class: 'form-element',
      label: this.getLabel(),
      placeholder: this.question.ui?.placeholder,
      'onAir-focus': this.handleNumberFocus,
      'onAir-change': this.handleNumberChange,
      'onAir-blur': this.handleNumberBlur,
      type: type,
      'mask-value': IMask.PIPE_TYPE.UNMASKED,
      validation: this.validation,
      value: this.answer as string,
    }
    return <air-input {...attributes}></air-input>
  }

  private renderDecimalElement = (): JSX.Element => {
    const type: InputType = 'number'
    const attributes = {
      class: 'form-element',
      label: this.getLabel(),
      placeholder: this.question.ui?.placeholder,
      'onAir-focus': this.handleFocus,
      'onAir-change': this.handleChange,
      'onAir-blur': this.handleBlur,
      type: type,
      'mask-value': IMask.PIPE_TYPE.UNMASKED,
      validation: this.validation,
      value: this.answer as string,
      decimals: 4,
    }
    return <air-input {...attributes}></air-input>
  }

  private toggleIncludes =
    (key: AnswerValue) =>
    (values: (string | boolean)[]): boolean => {
      if (!(typeof key === 'string' || typeof key === 'boolean')) return false
      const localKey = typeof key === 'string' ? key.toLocaleLowerCase() : key
      return values.includes(localKey)
    }

  private getToggleTrueOption = (options: QuestionOption[]): QuestionOption => {
    const trueOption = options.find((option: QuestionOption) =>
      this.toggleIncludes(option.key)(this.toggleTrueValues),
    )
    return trueOption ? trueOption : options[0]
  }

  private getToggleFalseOption = (options: QuestionOption[]): QuestionOption => {
    const falseOption = options.find((option: QuestionOption) =>
      this.toggleIncludes(option.key)(this.toggleFalseValues),
    )
    return falseOption ? falseOption : options[1]
  }

  private getBooleanValueByAnswer = (options: QuestionOption[]): boolean | undefined => {
    if (this.answer === undefined || this.answer === UnansweredValue) return undefined
    if (this.toggleIncludes(this.answer)(this.toggleTrueValues)) {
      return true
    }
    if (this.toggleIncludes(this.answer)(this.toggleFalseValues)) {
      return false
    }
    return options.findIndex((option: QuestionOption) => option.key === this.answer) === 0
  }

  private renderSelectElement = (): JSX.Element => {
    const commonAttributes = {
      label: this.getLabel(),
      class: 'form-element',
      validation: this.validation,
    }
    if (
      this.question.ui.control === QuestionUIControlType.Toggle &&
      this.question.options?.length === 2
    ) {
      const toggleAttributes = {
        ...commonAttributes,
        'onAir-focus': this.handleSelectToggleFocus,
        'onAir-change': this.handleSelectToggleChange,
        'onAir-blur': this.handleSelectToggleBlur,
        value: this.getBooleanValueByAnswer(this.question.options),
        trueLabel: this.getToggleTrueOption(this.question.options).label,
        falseLabel: this.getToggleFalseOption(this.question.options).label,
      }
      return <air-toggle {...toggleAttributes}></air-toggle>
    }

    const options = this.question.options?.map((item) => ({
      value: item.key,
      label: item.label,
    }))
    const attributes = {
      ...commonAttributes,
      placeholder: this.question.ui?.placeholder,
      options,
      value: this.answer as string,
      'onAir-focus': this.handleSelectFocus,
      'onAir-change': this.handleSelectChange,
      'onAir-blur': this.handleSelectBlur,
    }
    return <air-select {...attributes}></air-select>
  }

  private renderCurrencyElement = (): JSX.Element => {
    const type: InputType = 'currency'
    const attributes = {
      class: 'form-element',
      label: this.getLabel(),
      placeholder: this.question.ui?.placeholder,
      'onAir-focus': this.handleFocus,
      'onAir-change': this.handleChange,
      'onAir-blur': this.handleBlur,
      type: type,
      'mask-value': IMask.PIPE_TYPE.UNMASKED,
      validation: this.validation,
      value: this.answer as string,
    }
    return <air-input {...attributes}></air-input>
  }

  private renderDateElement = (): JSX.Element => {
    const attributes = {
      class: 'form-element',
      label: this.getLabel(),
      placeholder: this.question.ui?.placeholder,
      'onAir-focus': this.handleDatepickerFocus,
      'onAir-change': this.handleDatepickerChange,
      'onAir-blur': this.handleDatepickerBlur,
      validation: this.validation,
      value: this.dateFormatTransformReverse(this.answer as string),
    }
    return <air-datepicker {...attributes}></air-datepicker>
  }

  private renderDateYearElement = (): JSX.Element => {
    const mode: DatepickerMode = 'year'
    const attributes = {
      class: 'form-element',
      label: this.getLabel(),
      placeholder: this.question.ui?.placeholder,
      'onAir-focus': this.handleFocus,
      'onAir-change': this.handleChange,
      'onAir-blur': this.handleBlur,
      validation: this.validation,
      mode: mode,
      value: this.answer as string,
    }
    return <air-datepicker {...attributes}></air-datepicker>
  }

  private renderBooleanElement = (): JSX.Element => {
    const value = this.answer === true ? true : this.answer === false ? false : undefined
    const attributes = {
      class: 'form-element',
      label: this.getLabel(),
      validation: this.validation,
      value,
      'onAir-focus': this.handleBooleanFocus,
      'onAir-change': this.handleBooleanChange,
      'onAir-blur': this.handleBooleanBlur,
    }

    return <air-toggle {...attributes}></air-toggle>
  }

  private renderMultiSelectElement = (): JSX.Element => {
    const control = this.question.ui.control
    const hasPresentValidationRule = this.question.validation?.some(
      (rule) => rule.type === QuestionValidationRuleType.Present,
    )

    const checkboxAnswer = this.answer

    if (!isUndefined(control) && control === QuestionUIControlType.Dropdown) {
      const options = this.question.options?.map((item) => {
        if (item.key === NoneSelected) {
          return {
            ...item,
            selected: Array.isArray(checkboxAnswer) && checkboxAnswer.length === 0,
          }
        }
        return {
          ...item,
          selected:
            Array.isArray(checkboxAnswer) && checkboxAnswer.some((option) => option === item.key),
        }
      })

      // If component is not being used within a Questionnaire, also add
      // NoneSelected to the options for consistency
      if (
        options &&
        !options.some((option) => option.key === NoneSelected) &&
        !hasPresentValidationRule &&
        this.question.required
      ) {
        options.unshift({
          key: NoneSelected,
          label: 'None apply',
          selected: false,
        })
      }

      const attributes = {
        class: 'form-element',
        'onAir-change': this.handleMultiSelectDropdownChange,
        'onAir-focus': this.handleMultiSelectDropdownFocus,
        'onAir-blur': this.handleMultiSelectDropdownBlur,
        options,
        placeholder: this.question.ui?.placeholder,
        validation: this.validation,
      }
      return <air-dropdown-multi {...attributes}></air-dropdown-multi>
    }

    const options: CheckboxOption[] | undefined =
      checkboxAnswer === null || checkboxAnswer === undefined || !Array.isArray(checkboxAnswer)
        ? this.question.options?.map((item) => ({
            name: item.key.toString(),
            optionKey: item.key,
            label: item.label,
            checked: false,
          }))
        : this.question.options?.map((item) => ({
            name: item.key.toString(),
            optionKey: item.key,
            label: item.label,
            checked: checkboxAnswer && checkboxAnswer.some((option) => option === item.key),
          }))

    // If component is not being used within a Questionnaire, also add
    // NoneSelected to the options for consistency
    if (
      options &&
      !options.some((option) => option.name === NoneSelected) &&
      !hasPresentValidationRule &&
      this.question.required
    ) {
      options.push({
        name: NoneSelected,
        optionKey: NoneSelected,
        label: 'None of the above',
      })
    }

    const noneOfTheAboveOption = options?.find((option) => option.name === NoneSelected)
    if (noneOfTheAboveOption) {
      noneOfTheAboveOption.checked = Array.isArray(this.answer) && this.answer.length === 0
    }

    const attributes = {
      class: 'form-element',
      'onAir-change': this.handleCheckboxGroupChange,
      'onAir-focus': this.handleCheckboxGroupFocus,
      options,
      validation: this.validation,
    }

    return <air-checkbox-group {...attributes}></air-checkbox-group>
  }

  private renderProductTypeElement = (): JSX.Element => {
    const checkboxAnswer = this.answer as string[]
    const options: CheckboxOption[] | undefined = this.question.options?.map((item) => ({
      name: item.key.toString(),
      optionKey: item.key,
      label: item.label,
      checked: checkboxAnswer && checkboxAnswer.includes(item.key.toString()),
    }))
    const attributes = {
      class: 'form-element',
      'onAir-change': this.handleCheckboxGroupChange,
      'onAir-focus': this.handleCheckboxGroupFocus,
      options,
      validation: this.validation,
    }
    return <air-checkbox-group {...attributes}></air-checkbox-group>
  }

  private renderWorkersCompensationClassCodeElement = (): JSX.Element => {
    const attributes = {
      class: 'form-element',
      label: this.getLabel(),
      placeholder: this.question.ui?.placeholder,
      'onAir-change': this.handleSelectChange,
      validation: this.validation,
      workersCompensationClasses: this.workersCompensationClasses,
    }
    return <air-workers-compensation-class {...attributes}></air-workers-compensation-class>
  }

  private renderAddressElement = (): JSX.Element => {
    const attributes = {
      class: 'form-element',
      'onAir-address-change': this.handleAddressChange,
      'onAir-address-focus': this.handleAddressFocus,
      validation: this.validation,
      value: this.answer as Address,
    }
    return <air-address {...attributes}></air-address>
  }

  private renderBusinessClassElement = (): JSX.Element => {
    const attributes = {
      class: 'form-element first-level',
      'onAir-business-class-change': this.handleSelectChange,
      'onAir-business-class-focus': this.handleSelectFocus,
      businessClasses: this.businessClasses,
      label: this.getLabel(),
      placeholder: this.question.ui?.placeholder,
      value: this.answer as string,
      validation: this.validation,
    }
    return <air-business-class {...attributes}></air-business-class>
  }

  private renderVoidElement = (): JSX.Element => {
    return ``
  }

  private handleUnsupportedType(): never {
    throw new Error(`component for question ${this.question.id} does not exist`)
  }

  private renderFormElement = (): JSX.Element | never => {
    const questionRenders: Record<QuestionType, () => JSX.Element> = {
      [QuestionType.SingleLine]: this.renderSingleLineElement,
      [QuestionType.MultiLine]: this.renderMultiLineElement,
      [QuestionType.Number]: this.renderNumberElement,
      [QuestionType.Decimal]: this.renderDecimalElement,
      [QuestionType.Select]: this.renderSelectElement,
      [QuestionType.Currency]: this.renderCurrencyElement,
      [QuestionType.DateYear]: this.renderDateYearElement,
      [QuestionType.Date]: this.renderDateElement,
      [QuestionType.Boolean]: this.renderBooleanElement,
      [QuestionType.MultiSelect]: this.renderMultiSelectElement,
      [QuestionType.ProductType]: this.renderProductTypeElement,
      [QuestionType.WcClassCode]: this.renderWorkersCompensationClassCodeElement,
      [QuestionType.WorkersCompensationClassCode]: this.renderWorkersCompensationClassCodeElement,
      [QuestionType.Void]: this.renderVoidElement,
      [QuestionType.Address]: this.renderAddressElement,
      [QuestionType.ClassOfBusiness]: this.renderBusinessClassElement,
    }
    return questionRenders[this.question.type] !== undefined
      ? questionRenders[this.question.type]()
      : this.handleUnsupportedType()
  }

  render(): JSX.Element {
    return (
      <div class="question">
        {this.question?.ui?.hint && this.isMobile && (
          <air-icon class="info" name="info" onClick={this.handleMobileInfoClick}></air-icon>
        )}
        {!this.preferQuestionWithinGroup && this.question.question && (
          <h2 innerHTML={this.markdownToString()}></h2>
        )}
        {this.question?.questionWithinGroup && (
          <p class="group-title">{this.question.questionWithinGroup}</p>
        )}
        {this.question?.ui?.description && (
          <p class="description">{this.question.ui.description}</p>
        )}
        <div class="form-element-wrapper">{this.renderFormElement()}</div>
      </div>
    )
  }
}
